import { IsPdfLike, Picture } from 'smg/picture';
import * as strings from 'smg/strings';
import i18n from './i18n';

// One picture behind a row. `src` empty means the file has no on-screen
// rendition, and the thumb shows `format` instead.
export interface ProofItem {
    src: string;
    alt: string;
    format?: string;
    mime?: string;
    originalURL?: string;
}

export interface ProofStackOpts {
    size?: 'xs' | 'sm' | 'md' | 'lg';
    // Pictures drawn before the rest collapse into a +n chip.
    max?: number;
    layout?: 'fan' | 'row' | 'grid';
    shape?: 'circle';
    // The row's event kind, pinned over the first picture.
    badge?: string;
    badgeTone?: string;
    // Opens the artwork full size through the site's lightbox. Never inside an <a>.
    enlarge?: boolean;
}

const thumbWidth = { xs: 28, sm: 40, md: 56, lg: 72 };

// Fancybox groups by attribute value, so each stack needs its own id or two
// rows' artwork would open as one gallery.
let groupSeq = 0;

function thumbClass(size: NonNullable<ProofStackOpts['size']>, shapeClass: string): string {
    return ['proof-thumb', size === 'md' ? '' : `proof-thumb-${size}`, shapeClass].filter(Boolean).join(' ');
}

// The lightbox reads its gallery off these anchors, so every picture in a stack
// gets one — including the ones the stack does not draw.
function fancyboxAnchor(item: ProofItem, group: string): HTMLAnchorElement {
    const $a = document.createElement('a');
    $a.href = item.originalURL || item.src;
    $a.dataset.fancybox = group;
    $a.dataset.caption = item.alt;
    return $a;
}

// Short enough to read inside a 28px thumb, so this is not the Files page's
// fileFormat() — that one expands .ai to "Illustrator" for a subtitle line.
export function ProofFormat(name: string, mime: string): string {
    const [base, rawExt, found] = strings.CutLast(name, '.');
    const ext = rawExt.toUpperCase();
    if (found && base && /^[A-Z0-9]{1,4}$/.test(ext)) {
        return ext === 'JPEG' ? 'JPG' : ext;
    }
    switch (mime) {
    case 'application/pdf':
        return 'PDF';
    case 'application/postscript':
        return 'AI';
    case 'image/vnd.adobe.photoshop':
        return 'PSD';
    default: {
        const [, subtype, ok] = strings.Cut(mime, '/');
        return (ok ? subtype : mime).slice(0, 4).toUpperCase();
    }
    }
}

const formatTones: Record<string, string> = {
    PDF: 'danger',
    AI: 'warning',
    EPS: 'warning',
    PSD: 'warning',
};

function drawThumb(item: ProofItem, size: NonNullable<ProofStackOpts['size']>, shapeClass: string, enlarge: boolean, group: string): HTMLElement {
    let $thumb: HTMLElement;
    if (enlarge && item.src) {
        const $a = fancyboxAnchor(item, group);
        $a.setAttribute('aria-label', item.alt);
        $thumb = $a;
    } else {
        $thumb = document.createElement('span');
    }
    $thumb.className = thumbClass(size, shapeClass);

    if (item.src) {
        const $img = document.createElement('img');
        $img.alt = item.alt;
        // Picture() looks for the fancybox anchor through the <img>, so the
        // <img> has to be in the DOM tree before it wraps it in a <picture>.
        $thumb.append($img);
        let pdfSrc: string | undefined;
        if (item.mime && IsPdfLike(item.mime, item.alt, item.originalURL ?? '')) {
            pdfSrc = item.originalURL;
        }
        void Picture($img, item.src, {
            displayWidth: thumbWidth[size],
            alt: item.alt,
            mime: item.mime,
            pdfSrc,
            fancyboxThumb: enlarge,
        });
        return $thumb;
    }

    if (item.format) {
        const $fmt = document.createElement('span');
        const tone = formatTones[item.format] ?? 'neutral';
        $fmt.className = `proof-thumb-fmt proof-thumb-fmt-${tone}`;
        $fmt.textContent = item.format;
        $thumb.append($fmt);
        $thumb.setAttribute('role', 'img');
        $thumb.setAttribute('aria-label', item.alt);
        return $thumb;
    }

    const $icon = document.createElement('span');
    $icon.className = 'material-symbols-rounded';
    $icon.setAttribute('aria-hidden', 'true');
    $icon.textContent = 'image';
    $thumb.append($icon);
    return $thumb;
}

function drawBadge($thumb: HTMLElement, badge: string, tone: string): HTMLElement {
    const $wrap = document.createElement('span');
    $wrap.className = 'proof-thumb-wrap';
    const $badge = document.createElement('span');
    $badge.className = `proof-thumb-badge text-${tone}`;
    const $icon = document.createElement('span');
    $icon.className = 'material-symbols-rounded';
    $icon.setAttribute('aria-hidden', 'true');
    $icon.textContent = badge;
    $badge.append($icon);
    $wrap.append($thumb, $badge);
    return $wrap;
}

// A row's artwork: one picture in the common case, several in a real one. Past
// `max` the rest collapse into a +n chip, so a row's width never tracks the
// proof count — a dashboard column can be as narrow as 19rem.
export function ProofStack(items: ProofItem[], opts: ProofStackOpts = {}): HTMLElement | undefined {
    if (items.length === 0) {
        return undefined;
    }
    const size = opts.size ?? 'md';
    const max = opts.max ?? 3;
    const layout = opts.layout ?? (size === 'xs' || size === 'sm' ? 'fan' : 'row');
    const shapeClass = opts.shape === 'circle' ? 'proof-thumb-circle' : '';
    const enlarge = opts.enlarge === true;
    const group = `proof-art-${++groupSeq}`;

    const $stack = document.createElement('span');
    $stack.className = ['proof-stack', `proof-stack-${layout}`, layout === 'fan' ? `proof-stack-${size}` : ''].filter(Boolean).join(' ');

    const shown = items.slice(0, max);
    const overflow = items.slice(max);
    for (const [i, item] of shown.entries()) {
        const $thumb = drawThumb(item, size, shapeClass, enlarge, group);
        if (i === 0 && opts.badge) {
            $stack.append(drawBadge($thumb, opts.badge, opts.badgeTone ?? 'primary'));
        } else {
            $stack.append($thumb);
        }
    }

    if (overflow.length > 0) {
        // The chip IS the first undrawn picture's link, so the gallery it opens
        // carries every proof exactly once.
        const [first, ...rest] = overflow;
        let $more: HTMLElement;
        if (enlarge && first.src) {
            $more = fancyboxAnchor(first, group);
        } else {
            $more = document.createElement('span');
        }
        $more.className = thumbClass(size, shapeClass);
        const moreLabel = overflow.length === 1 ?
            i18n('accountMoreArtworkOne') :
            i18n('accountMoreArtworkMany').replace('%s', String(overflow.length));
        $more.setAttribute('aria-label', moreLabel);
        const $count = document.createElement('span');
        $count.className = 'proof-thumb-more';
        $count.textContent = `+${overflow.length}`;
        $more.append($count);
        $stack.append($more);

        // The lightbox only knows the pictures it can see in the group, so the
        // rest ride along as links with nothing drawn.
        for (const item of rest) {
            if (!enlarge || !item.src) {
                continue;
            }
            const $hidden = fancyboxAnchor(item, group);
            $hidden.className = 'd-none';
            $stack.append($hidden);
        }
    }

    return $stack;
}
